import { useState } from 'react';

interface Property {
  id: number;
  title: string;
  image: string;
  photos?: string[];
}

interface PropertyGalleryProps {
  property: Property;
}

export default function PropertyGallery({ property }: PropertyGalleryProps) {
  const images = property.photos && property.photos.length > 0 ? property.photos : [property.image];
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  const prevLightbox = () => setLightboxIndex(i => (i - 1 + images.length) % images.length);
  const nextLightbox = () => setLightboxIndex(i => (i + 1) % images.length);

  const renderGallery = () => {
    const count = images.length;

    // 1 sola foto
    if (count === 1) {
      return (
        <div
          className="rounded-2xl overflow-hidden cursor-pointer group relative"
          style={{ height: '480px' }}
          onClick={() => openLightbox(0)}
        >
          <img src={images[0]} alt={property.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full w-12 h-12 flex items-center justify-center">
              <i className="ri-zoom-in-line text-gray-900 text-xl"></i>
            </div>
          </div>
        </div>
      );
    }

    // 2 fotos
    if (count === 2) {
      return (
        <div className="grid grid-cols-2 gap-2 sm:gap-3 rounded-2xl overflow-hidden" style={{ height: '480px' }}>
          {images.map((img, idx) => (
            <div key={idx} className="relative cursor-pointer group overflow-hidden" onClick={() => openLightbox(idx)}>
              <img src={img} alt={`${property.title} foto ${idx + 1}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full w-10 h-10 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-gray-900 text-lg"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // 3 fotos
    if (count === 3) {
      return (
        <div className="grid grid-cols-2 gap-2 sm:gap-3 rounded-2xl overflow-hidden" style={{ height: '480px' }}>
          <div className="relative cursor-pointer group overflow-hidden row-span-2" onClick={() => openLightbox(0)}>
            <img src={images[0]} alt={`${property.title} foto 1`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full w-10 h-10 flex items-center justify-center">
                <i className="ri-zoom-in-line text-gray-900 text-lg"></i>
              </div>
            </div>
          </div>
          {images.slice(1, 3).map((img, idx) => (
            <div key={idx} className="relative cursor-pointer group overflow-hidden" onClick={() => openLightbox(idx + 1)}>
              <img src={img} alt={`${property.title} foto ${idx + 2}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full w-10 h-10 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-gray-900 text-lg"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // 4+ fotos (layout original)
    return (
      <div className="grid grid-cols-4 grid-rows-2 gap-2 sm:gap-3 rounded-2xl overflow-hidden" style={{ height: '480px' }}>
        {/* Imagen principal grande */}
        <div
          className="col-span-2 row-span-2 relative cursor-pointer group overflow-hidden"
          onClick={() => openLightbox(activeIndex)}
        >
          <img
            src={images[activeIndex]}
            alt={property.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full w-12 h-12 flex items-center justify-center">
              <i className="ri-zoom-in-line text-gray-900 text-xl"></i>
            </div>
          </div>
          <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">
            {activeIndex + 1} / {images.length}
          </div>
        </div>

        {/* Miniaturas */}
        {images.slice(0, 4).map((img, idx) => (
          <div
            key={idx}
            className="relative cursor-pointer group overflow-hidden"
            onClick={() => {
              if (idx === 3 && images.length > 4) {
                openLightbox(3);
              } else {
                setActiveIndex(idx);
              }
            }}
          >
            <img
              src={img}
              alt={`${property.title} foto ${idx + 1}`}
              className={`w-full h-full object-cover object-top transition-all duration-300 ${activeIndex === idx ? 'brightness-100' : 'brightness-75 group-hover:brightness-90'}`}
            />
            {idx === 3 && images.length > 4 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-center text-white">
                  <i className="ri-image-line text-2xl mb-1 block"></i>
                  <span className="text-sm font-semibold">+{images.length - 4} fotos</span>
                </div>
              </div>
            )}
            {activeIndex === idx && (
              <div className="absolute inset-0 ring-2 ring-red-500 ring-inset pointer-events-none"></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {renderGallery()}

      {/* Tira de miniaturas */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => { setActiveIndex(idx); }}
              className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${activeIndex === idx ? 'border-red-500' : 'border-transparent opacity-60 hover:opacity-100'}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
              className="absolute left-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
          )}

          <div
            className="max-w-5xl max-h-[85vh] mx-16 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex]}
              alt={`${property.title} foto ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
            />
          </div>

          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
              className="absolute right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          )}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {lightboxIndex + 1} / {images.length}
          </div>

          {images.length > 1 && (
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                  className={`w-12 h-9 rounded overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${lightboxIndex === idx ? 'border-white' : 'border-transparent opacity-50 hover:opacity-80'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
